import React, { FC, useState } from "react";
import { FaStar } from "react-icons/fa";
import { LuPencil, LuUser } from "react-icons/lu";
import { useParams } from "react-router-dom";

import { Card } from "../../../../../../../../components/card/Card";
import { Avatar } from "../../../../../../../../components/newUI/avatar";
import { Button } from "../../../../../../../../components/newUI/button";
import { getLinkedFirm } from "../../../../../../../../sessionStorage/sessionStorage";
import { useGetOrgContactQuery } from "../../../../../../../../slices/ContactsSlice";
import { PortalInfoRow } from "../../../../../../../clientPortal/PortalInfoRow";
import { useFilledCustomFields } from "../../../../../../../customFields/useFilledCustomFields";
import { useFirmRegion } from "../../../../../../../region/useFirmRegion";
import { useClientEmailCompose } from "../../../../../../clientEmail/useClientEmailCompose";
import { formatDateOnly } from "../../../../../../utils/dateOnly";
import { EditOrganizationModal } from "../../../../modal/EditOrganization.modal";

type InfoRowProps = {
  label: string;
  children: React.ReactNode;
  withBorder?: boolean;
};

const InfoRow = ({ label, children, withBorder = true }: InfoRowProps) => (
  <div
    className={`flex min-w-0 max-w-full gap-[13px] py-3.5 ${
      withBorder ? "border-t border-[#F0F1F4]" : ""
    }`}>
    <div className={"min-w-0 flex-1"}>
      <div className={"mb-1 text-[12.5px] font-bold text-[#8A92A3]"}>
        {label}
      </div>
      <div
        className={
          "text-[14px] font-semibold leading-[1.45] text-[#232A38] [overflow-wrap:anywhere]"
        }>
        {children}
      </div>
    </div>
  </div>
);

const getAddressText = (
  addresses:
    | Array<{
        line1?: string | null;
        city?: string | null;
        region?: string | null;
        postalCode?: string | null;
      }>
    | undefined,
) =>
  addresses
    ?.map((item) =>
      [
        item.line1,
        [item.city, item.region].filter(Boolean).join(", "),
        item.postalCode,
      ]
        .filter(Boolean)
        .join("\n"),
    )
    .filter(Boolean)
    .join("\n\n") || "";

export const OrganizationInfo: FC = () => {
  const region = useFirmRegion();
  const { organizationId } = useParams();
  const { data: organization } = useGetOrgContactQuery({
    id: organizationId || "",
    orgId: getLinkedFirm()?.orgId || "",
  });

  const customFieldRows = useFilledCustomFields(
    organization?.customerId || organization?.businessCustomers?.[0]?.id,
    "organization",
  );
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const { openCompose, composeModal } = useClientEmailCompose();
  const contactPersonName = [
    organization?.contactPersons?.firstName,
    organization?.contactPersons?.lastName,
  ]
    .filter(Boolean)
    .join(" ");
  const addressText = getAddressText(organization?.addresses);
  const yearEnd = [organization?.yearEndMonth, organization?.yearEndDay]
    .filter(Boolean)
    .join(" ");
  const gstHst = [
    organization?.gstHstNumber,
    organization?.gstHstReportingPeroid,
  ]
    .filter(Boolean)
    .join(" · ");
  const pst = [organization?.pstNumber, organization?.pstReportingPeroid]
    .filter(Boolean)
    .join(" · ");
  // Empty rows (older saves of an empty Edit box) are not shown, so an
  // organization without a contact person has no empty box (QA batch 16, SET-035).
  const shownEmails = (organization?.emails ?? []).filter(
    (item) => !!item.emailAddress?.trim(),
  );
  const shownPhones = (organization?.phoneNumbers ?? []).filter(
    (item) => !!item.number?.trim(),
  );
  const hasContactPerson =
    !!contactPersonName.trim() || !!shownEmails.length || !!shownPhones.length;

  return (
    <Card extraClasses={"overflow-hidden"}>
      <div className={"flex min-w-0 flex-col p-6 pb-5 md:h-[792px]"}>
        <div className={"mb-4 flex items-center justify-between gap-2"}>
          <div className={"flex min-w-0 items-center gap-3.5"}>
            <Avatar
              id={
                organization?.id ||
                organizationId ||
                organization?.companyName ||
                "organization"
              }
              fullName={organization?.companyName || ""}
              color={"secondary"}
              size={"sm"}
              avatarClassNames={"h-9 w-9 shrink-0 text-[13px] font-bold"}
            />
            <div className={"min-w-0 flex-1"}>
              <div
                className={
                  "text-[19px] font-bold leading-[1.2] tracking-[-0.35px] text-[#1A2030] [overflow-wrap:break-word]"
                }>
                {organization?.companyName || "-"}
              </div>
            </div>
          </div>
          <Button
            type={"button"}
            variant={"outline"}
            size={"sm"}
            autoWidth
            leftIcon={<LuPencil size={13} />}
            className={
              "h-[34px] shrink-0 rounded-[9px] border-[#E4E7EC] px-3.5 text-[12.5px] font-semibold text-[#4A5262] hover:border-[#D3D8E0] hover:bg-[#F7F8FA] hover:text-[#1F2433]"
            }
            onClick={() => setIsOpenEdit(true)}>
            Edit
          </Button>
        </div>

        <PortalInfoRow customerId={organization?.customerId} />

        <div className={"mx-[-24px] min-h-0 flex-1 overflow-y-auto px-6"}>
          <InfoRow label={"Display name"} withBorder={false}>
            {/* Empty display name = the company name is used (QA batch 11). */}
            {organization?.dba?.trim() || organization?.companyName || "-"}
          </InfoRow>

          {hasContactPerson && (
            <div
              className={
                "mx-[-24px] my-4 min-w-0 overflow-hidden border-y border-[#E6E8ED] bg-[#F5F6F8] px-6 py-[15px]"
              }>
              <div className={"mb-3 flex items-center gap-2"}>
                <LuUser size={13} className={"text-[#9097A8]"} />
                <span
                  className={
                    "text-[11.5px] font-extrabold uppercase tracking-[0.5px] text-[#8A92A3]"
                  }>
                  Contact person
                </span>
              </div>
              {!!contactPersonName && (
                <div className={"mb-3"}>
                  <span
                    className={
                      "block text-[14.5px] font-bold leading-[1.3] text-[#1F2433] [overflow-wrap:anywhere]"
                    }>
                    {contactPersonName}
                  </span>
                </div>
              )}
              {shownPhones.map((item) => (
                <div
                  key={item.id}
                  className={"min-w-0 border-t border-[#E6E8ED] py-[7px]"}>
                  <div
                    className={"mb-[3px] text-[12px] font-bold text-[#9097A8]"}>
                    {item.phoneNumberTypeEnum?.phoneNumberType || "Phone"}
                  </div>
                  <div
                    className={
                      "text-[13.5px] font-semibold text-[#232A38] [overflow-wrap:anywhere]"
                    }>
                    {item.number}
                  </div>
                </div>
              ))}
              {shownEmails.map((item) => (
                <div
                  key={item.id}
                  className={"min-w-0 border-t border-[#E6E8ED] py-[7px]"}>
                  <div
                    className={
                      "mb-[3px] flex items-center gap-1 text-[12px] font-bold text-[#9097A8]"
                    }>
                    Email
                    {item.isPrimaryEmail && (
                      <FaStar
                        size={12}
                        className={"shrink-0 text-[#F5A524]"}
                        title={"Primary email"}
                      />
                    )}
                  </div>
                  <button
                    type={"button"}
                    title={`Write an email to ${item.emailAddress || ""}`}
                    className={
                      "inline-flex max-w-full text-left text-[13.5px] font-semibold leading-[1.45] text-[#232A38] [overflow-wrap:anywhere] hover:text-[#6B4EFF] hover:underline"
                    }
                    onClick={() =>
                      openCompose({
                        email: item.emailAddress,
                        customerId:
                          organization?.customerId ||
                          organization?.businessCustomers?.[0]?.id,
                        name: organization?.companyName,
                        businessContactId: organization?.id,
                        contactPersonFirstName:
                          organization?.contactPersons?.firstName,
                        contactPersonLastName:
                          organization?.contactPersons?.lastName,
                      })
                    }>
                    {item.emailAddress}
                  </button>
                </div>
              ))}
            </div>
          )}

          {!!addressText && (
            <InfoRow label={"Address"}>
              <span
                className={
                  "block whitespace-pre-wrap [overflow-wrap:anywhere]"
                }>
                {addressText}
              </span>
            </InfoRow>
          )}
          {!!organization?.incorporationDate && (
            <InfoRow label={"Incorporation date"}>
              {formatDateOnly(organization.incorporationDate)}
            </InfoRow>
          )}
          {!!yearEnd && <InfoRow label={"Year end"}>{yearEnd}</InfoRow>}
          {!!organization?.businessNumber && (
            <InfoRow label={region.businessId.label}>
              {organization.businessNumber}
            </InfoRow>
          )}
          {!!gstHst && <InfoRow label={region.salesTaxLabel}>{gstHst}</InfoRow>}
          {!!pst && <InfoRow label={"PST number"}>{pst}</InfoRow>}
          {organization?.organizationType?.organizationType && (
            <InfoRow label={"Organization type"}>
              {organization.organizationType.organizationType}
            </InfoRow>
          )}
          {customFieldRows.map((field) => (
            <InfoRow key={field.id} label={field.label}>
              <span
                className={
                  "block whitespace-pre-wrap [overflow-wrap:anywhere]"
                }>
                {field.value}
              </span>
            </InfoRow>
          ))}
          <InfoRow label={"Contact type"}>
            {organization?.contactType?.contactType || "-"}
          </InfoRow>
          {organization?.additionalInfo && (
            <InfoRow label={"Additional info"}>
              <span
                className={
                  "block whitespace-pre-wrap [overflow-wrap:anywhere]"
                }>
                {organization.additionalInfo}
              </span>
            </InfoRow>
          )}
        </div>
        {composeModal}
        {isOpenEdit && (
          <EditOrganizationModal
            isOpen={isOpenEdit}
            onClose={() => setIsOpenEdit(false)}
          />
        )}
      </div>
    </Card>
  );
};
